import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '../hooks/useAuth';
import { ToastProvider } from '../context/ToastContext';
import { SettingsProvider } from '../context/SettingsContext';
import { ConnectionProvider } from '../context/ConnectionContext';
import { PermissionProvider } from '../context/PermissionContext';
import { ClientWrapper } from '../app/components/ClientWrapper';
import { NetworkStatus } from '../components/NetworkStatus';

// Layouts
import RootLayout from '../app/layout';

// Pages
import RootPage from '../app/page';
import LoginPage from '../app/login/page';
import LogoutPage from '../app/logout/page';
import DashboardPage from '../app/dashboard/page';

// Quotations
import QuotationsPage from '../app/quotations/page';
import QuotationDetailPage from '../app/quotations/detail/page';

// Events
import EventsPage from '../app/events/page';
import EventDetailPage from '../app/events/detail/page';

// Enquiries
import EnquiriesPage from '../app/enquiries/page';
import EnquiryDetailPage from '../app/enquiries/detail/page';

// Clients
import ClientsPage from '../app/clients/page';

// Vendors
import VendorsPage from '../app/vendors/page';
import VendorDetailPage from '../app/vendors/detail/page';

// Staff/Users
import StaffPage from '../app/staff/page';

// Tasks
import TasksPage from '../app/tasks/page';

// Calendar
import CalendarPage from '../app/calendar/page';

// Invoices
import InvoicesPage from '../app/invoices/page';

// Expenses
import ExpensesPage from '../app/expenses/page';

// Masters
import ServicesPage from '../app/services/page';
import PackagesPage from '../app/packages/page';
import VenuesPage from '../app/venues/page';
import RoomsPage from '../app/rooms/page';
import RoomBookingsPage from '../app/room-bookings/page';
import EventTypesPage from '../app/event-types/page';
import VendorTypesPage from '../app/vendor-types/page';
import ExpenseCategoriesPage from '../app/expense-categories/page';
import PaymentMethodsPage from '../app/payment-methods/page';

// Settings
import SettingsPage from '../app/settings/page';
import InvoiceSettingsPage from '../app/settings/invoice/page';

// Reports
import ReportsHubPage from '../app/reports/page';
import ReportsEventsPage from '../app/reports/events/page';
import ReportsEventDetailPage from '../app/reports/events/detail/page';
import ReportsExpensesPage from '../app/reports/expenses/page';
import VendorPerformancePage from '../app/reports/vendors/performance/page';
import VendorAssignmentsPage from '../app/reports/vendors/assignments/page';
import VendorComparisonPage from '../app/reports/vendors/comparison/page';
import VendorCommissionsPage from '../app/reports/vendors/commissions/page';
import FinancialSummaryPage from '../app/reports/financial-summary/page';
import RevenueTrendsPage from '../app/reports/revenue/page';
import ClientAnalyticsPage from '../app/reports/clients/page';
import EnquiryFunnelPage from '../app/reports/enquiries/page';
import PackageAnalyticsPage from '../app/reports/packages/page';
import VenueUtilizationPage from '../app/reports/venues/page';

// Other
import BookingsPage from '../app/bookings/page';
import ClientPaymentsPage from '../app/client-payments/page';
import VendorPaymentsPage from '../app/vendor-payments/page';
import PaymentLogsPage from '../app/payment-logs/page';
import ActivityLogsPage from '../app/activity-logs/page';
import DataManagementPage from '../app/data-management/page';
import FrontendSettingsPage from '../app/frontend-settings/page';
import ImpersonatePage from '../app/impersonate/page';
import InstallPage from '../app/install/page';

function App() {
  return (
    <ConnectionProvider>
      <SettingsProvider>
        <ToastProvider>
          <AuthProvider>
            <PermissionProvider>
            <RootLayout>
              <ClientWrapper>
                <NetworkStatus />
                <Routes>
                  {/* Root */}
                  <Route path="/" element={<RootPage />} />
                  
                  {/* Auth */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/logout" element={<LogoutPage />} />
                  <Route path="/impersonate" element={<ImpersonatePage />} />
                  <Route path="/install" element={<InstallPage />} />
                  
                  {/* Dashboard */}
                  <Route path="/dashboard" element={<DashboardPage />} />
                  
                  {/* Quotations */}
                  <Route path="/quotations" element={<QuotationsPage />} />
                  <Route path="/quotations/detail" element={<QuotationDetailPage />} />
                  
                  {/* Events */}
                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/events/detail" element={<EventDetailPage />} />
                  
                  {/* Enquiries */}
                  <Route path="/enquiries" element={<EnquiriesPage />} />
                  <Route path="/enquiries/detail" element={<EnquiryDetailPage />} />
                  
                  {/* Clients */}
                  <Route path="/clients" element={<ClientsPage />} />
                  
                  {/* Vendors */}
                  <Route path="/vendors" element={<VendorsPage />} />
                  <Route path="/vendors/detail" element={<VendorDetailPage />} />
                  
                  {/* Staff */}
                  <Route path="/staff" element={<StaffPage />} />
                  
                  {/* Tasks */}
                  <Route path="/tasks" element={<TasksPage />} />
                  
                  {/* Calendar */}
                  <Route path="/calendar" element={<CalendarPage />} />
                  
                  {/* Invoices */}
                  <Route path="/invoices" element={<InvoicesPage />} />
                  
                  {/* Expenses */}
                  <Route path="/expenses" element={<ExpensesPage />} />
                  
                  {/* Masters */}
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/packages" element={<PackagesPage />} />
                  <Route path="/venues" element={<VenuesPage />} />
                  <Route path="/rooms" element={<RoomsPage />} />
                  <Route path="/room-bookings" element={<RoomBookingsPage />} />
                  <Route path="/event-types" element={<EventTypesPage />} />
                  <Route path="/vendor-types" element={<VendorTypesPage />} />
                  <Route path="/expense-categories" element={<ExpenseCategoriesPage />} />
                  <Route path="/payment-methods" element={<PaymentMethodsPage />} />
                  
                  {/* Settings */}
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/invoice" element={<InvoiceSettingsPage />} />
                  
                  {/* Reports */}
                  <Route path="/reports" element={<ReportsHubPage />} />
                  <Route path="/reports/financial-summary" element={<FinancialSummaryPage />} />
                  <Route path="/reports/revenue" element={<RevenueTrendsPage />} />
                  <Route path="/reports/clients" element={<ClientAnalyticsPage />} />
                  <Route path="/reports/enquiries" element={<EnquiryFunnelPage />} />
                  <Route path="/reports/packages" element={<PackageAnalyticsPage />} />
                  <Route path="/reports/venues" element={<VenueUtilizationPage />} />
                  <Route path="/reports/events" element={<ReportsEventsPage />} />
                  <Route path="/reports/events/detail" element={<ReportsEventDetailPage />} />
                  <Route path="/reports/expenses" element={<ReportsExpensesPage />} />
                  <Route path="/reports/vendors/performance" element={<VendorPerformancePage />} />
                  <Route path="/reports/vendors/assignments" element={<VendorAssignmentsPage />} />
                  <Route path="/reports/vendors/comparison" element={<VendorComparisonPage />} />
                  <Route path="/reports/vendors/commissions" element={<VendorCommissionsPage />} />
                  
                  {/* Other */}
                  <Route path="/bookings" element={<BookingsPage />} />
                  <Route path="/client-payments" element={<ClientPaymentsPage />} />
                  <Route path="/vendor-payments" element={<VendorPaymentsPage />} />
                  <Route path="/payment-logs" element={<PaymentLogsPage />} />
                  <Route path="/activity-logs" element={<ActivityLogsPage />} />
                  <Route path="/data-management" element={<DataManagementPage />} />
                  <Route path="/frontend-settings" element={<FrontendSettingsPage />} />
                  
                  {/* Catch all - redirect to root */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </ClientWrapper>
            </RootLayout>
            </PermissionProvider>
          </AuthProvider>
        </ToastProvider>
      </SettingsProvider>
    </ConnectionProvider>
  );
}

export default App;
